import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAdminRfqsList, type AdminListStatus } from "../admin/api.js";
import type { ServiceCategory, Urgency } from "../rfqs/api.js";
import {
  serviceCategoryLabels,
  serviceCategoryOptions,
  urgencyLabels,
  urgencyOptions,
} from "../rfqs/api.js";
import { AdminCard, StatusBadge } from "./AdminShell.js";

const STATUS_TABS: { value: AdminListStatus; label: string }[] = [
  { value: "OPEN", label: "Open" },
  { value: "SUBMITTED", label: "Submitted" },
  { value: "IN_REVIEW", label: "In review" },
  { value: "CLOSED", label: "Closed" },
  { value: "CANCELLED", label: "Cancelled" },
];

export function AdminRfqs() {
  const [status, setStatus] = useState<AdminListStatus>("OPEN");
  const [pages, setPages] = useState<string[]>([]); // tokens for navigation history
  const [category, setCategory] = useState<ServiceCategory | "">("");
  const [urgency, setUrgency] = useState<Urgency | "">("");
  const [city, setCity] = useState("");
  const currentToken = pages[pages.length - 1];
  const query = useAdminRfqsList(status, currentToken);

  // OPEN view merges two statuses server-side and returns no pagination cursor.
  const paginated = status !== "OPEN";

  const filtered = useMemo(() => {
    const items = query.data?.items ?? [];
    const cityNeedle = city.trim().toLowerCase();
    return items.filter((rfq) => {
      if (category && rfq.serviceCategory !== category) return false;
      if (urgency && rfq.urgency !== urgency) return false;
      if (cityNeedle && !rfq.location.city.toLowerCase().includes(cityNeedle)) return false;
      return true;
    });
  }, [query.data, category, urgency, city]);

  return (
    <div className="space-y-3">
      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
        {STATUS_TABS.map((t) => {
          const active = t.value === status;
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => {
                setStatus(t.value);
                setPages([]);
              }}
              className={`min-h-[40px] shrink-0 rounded-full border px-3 py-1.5 text-sm ${
                active
                  ? "border-teal-700 bg-teal-700 text-white"
                  : "border-slate-200 bg-white text-slate-700"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <AdminCard>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <label className="flex flex-col text-xs text-slate-600">
            <span className="mb-1 uppercase tracking-wide">Category</span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as ServiceCategory | "")}
              className="min-h-[40px] rounded-md border border-slate-300 bg-white px-2 text-sm"
            >
              <option value="">All</option>
              {serviceCategoryOptions.map((c) => (
                <option key={c} value={c}>
                  {serviceCategoryLabels[c]}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col text-xs text-slate-600">
            <span className="mb-1 uppercase tracking-wide">Urgency</span>
            <select
              value={urgency}
              onChange={(e) => setUrgency(e.target.value as Urgency | "")}
              className="min-h-[40px] rounded-md border border-slate-300 bg-white px-2 text-sm"
            >
              <option value="">All</option>
              {urgencyOptions.map((u) => (
                <option key={u} value={u}>
                  {urgencyLabels[u]}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col text-xs text-slate-600">
            <span className="mb-1 uppercase tracking-wide">City</span>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="e.g. Austin"
              className="min-h-[40px] rounded-md border border-slate-300 bg-white px-2 text-sm"
            />
          </label>
        </div>
      </AdminCard>

      {query.isLoading && <p className="text-sm text-slate-500">Loading…</p>}
      {query.isError && (
        <p className="text-sm text-rose-700">
          Failed to load RFQs. {query.error instanceof Error ? query.error.message : ""}
        </p>
      )}

      {query.data && filtered.length === 0 && (
        <AdminCard>
          <p className="text-sm text-slate-600">
            {query.data.items.length === 0
              ? `No RFQs in ${status}.`
              : "No RFQs match the current filters."}
          </p>
        </AdminCard>
      )}

      <ul className="space-y-2">
        {filtered.map((rfq) => (
          <li key={`${rfq.customerSub}#${rfq.id}`}>
            <Link
              to={`/admin/rfqs/${encodeURIComponent(rfq.customerSub)}/${encodeURIComponent(rfq.id)}`}
              className="block"
            >
              <AdminCard>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-900">
                      {serviceCategoryLabels[rfq.serviceCategory]} · {urgencyLabels[rfq.urgency]}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-slate-500">
                      {rfq.location.city}, {rfq.location.state} {rfq.location.postalCode}
                    </p>
                    <p className="mt-1 line-clamp-2 text-sm text-slate-700">{rfq.description}</p>
                    <p className="mt-1 text-xs text-slate-400">
                      {new Date(rfq.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <StatusBadge status={rfq.status} />
                </div>
              </AdminCard>
            </Link>
          </li>
        ))}
      </ul>

      {paginated && (
        <div className="flex items-center justify-between pt-2">
          <button
            type="button"
            disabled={pages.length === 0}
            onClick={() => setPages((p) => p.slice(0, -1))}
            className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 disabled:opacity-40"
          >
            ← Previous
          </button>
          <button
            type="button"
            disabled={!query.data?.nextToken}
            onClick={() => {
              const tok = query.data?.nextToken;
              if (tok) setPages((p) => [...p, tok]);
            }}
            className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 disabled:opacity-40"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
